/**
 * MIT LICENSE
 *
 * Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/
 *
 * Permission is hereby granted, free of charge, to any person obtaining
 * a copy of this software and associated documentation files (the
 * "Software"), to deal in the Software without restriction, including
 * without limitation the rights to use, copy, modify, merge, publish,
 * distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to
 * the following conditions:
 *
 * The above copyright notice and this permission notice shall be
 * included in all copies or substantial portions of the Software.
 */

import React from 'react';
import RcUpload from 'rc-upload';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import {flushSync} from 'react-dom';
import {file2Obj, getFileItem, updateFileList} from './utils';
import K from '../../k';

const InternalUpload = (props, ref) => {
  const {
    type = 'button',
    fileList,
    onChange,
    onDrop,
    onDrag,
    onBatchStart,
    beforeUpload,
    disabled,
    children,
    style,
    contentStyle,
    wrapperStyle,
    maxCount,
    data = {},
    multiple = false,
    action = '',
    accept = '',
  } = props;

  const [mergedFileList, setMergedFileList] = useMergedState([], {
    value: fileList,
    postState: (list) => list ?? [],
  });

  const [dragState, setDragState] = React.useState('drop');
  var dragCounter = 0;

  React.useEffect(() => {
    document.addEventListener('dragenter', () => onFileDrop({type: 'dragenter'}));
    document.addEventListener('dragleave', () => onFileDrop({type: 'dragleave'}));

    return () => {
      // dispose
      document.removeEventListener('dragenter', () => onFileDrop({type: 'dragenter'}));
      document.removeEventListener('dragleave', () => onFileDrop({type: 'dragleave'}));
    };
  }, [ref, onFileDrop]);

  const upload = React.useRef(null);

  // Control mode will auto fill file uid if not provided
  React.useMemo(() => {
    const timestamp = Date.now();

    (fileList || []).forEach((file, index) => {
      if (!file.uid && !Object.isFrozen(file)) {
        file.uid = `__AUTO__${timestamp}_${index}__`;
      }
    });
  }, [fileList]);

  const onInternalChange = (
    file,
    changedFileList,
    event,
  ) => {
    let cloneList = [...changedFileList];

    // Cut to match count
    if (maxCount === 1) {
      cloneList = cloneList.slice(-1);
    } else if (maxCount) {
      cloneList = cloneList.slice(0, maxCount);
    }

    // Prevent React18 auto batch since input[upload] trigger process at same time
    // which makes fileList closure problem
    flushSync(() => {
      setMergedFileList(cloneList);
    });

    const changeInfo = {
      file: file,
      fileList: cloneList,
    };

    if (event) {
      changeInfo.event = event;
    }

    flushSync(() => {
      onChange?.(changeInfo);
    });
  };

  const mergedOnBatchStart = (batchFileInfoList) => {
    const objectFileList = batchFileInfoList.map((info) => file2Obj(info.file));

    // Concat new files with prev files
    let newFileList = [...mergedFileList];

    objectFileList.forEach((fileObj) => {
      // Replace file if exist
      newFileList = updateFileList(fileObj, newFileList);
    });

    objectFileList.forEach((fileObj, index) => {
      // Repeat trigger `onChange` event for compatible
      let triggerFileObj = fileObj;

      if (!batchFileInfoList[index].parsedFile) {
        // `beforeUpload` return false
        const {originFileObj} = fileObj;
        let clone;

        try {
          clone = new File([originFileObj], originFileObj.name, {
            type: originFileObj.type,
          });
        } catch (e) {
          clone = new Blob([originFileObj], {
            type: originFileObj.type,
          });
          clone.name = originFileObj.name;
          clone.lastModifiedDate = new Date();
          clone.lastModified = new Date().getTime();
        }

        clone.uid = fileObj.uid;
        triggerFileObj = clone;
      } else {
        // Inject `uploading` status
        fileObj.status = 'uploading';
      }

      onInternalChange(triggerFileObj, newFileList);
    });

    //#TODO if on batch start returns false, prevent default
    //see customRequest or beforeUpload
    onBatchStart?.(newFileList);
  };

  const onSuccess = (response, file, xhr) => {
    try {
      if (typeof response === 'string') {
        response = JSON.parse(response);
      }
    } catch (e) {
      /* do nothing */
    }

    // removed
    if (!getFileItem(file, mergedFileList)) {
      return;
    }

    const targetItem = file2Obj(file);
    targetItem.status = 'done';
    targetItem.percent = 100;
    targetItem.response = response;
    targetItem.xhr = xhr;

    const nextFileList = updateFileList(targetItem, mergedFileList);

    onInternalChange(targetItem, nextFileList);
  };

  const onProgress = (e, file) => {
    //HINT removed
    if (!getFileItem(file, mergedFileList)) {
      return;
    }

    const targetItem = file2Obj(file);
    targetItem.status = 'uploading';
    targetItem.percent = e.percent;

    const nextFileList = updateFileList(targetItem, mergedFileList);

    onInternalChange(targetItem, nextFileList, e);
  };

  const onError = (error, response, file) => {
    //HINT removed
    if (!getFileItem(file, mergedFileList)) {
      return;
    }

    const targetItem = file2Obj(file);
    targetItem.error = error;
    targetItem.response = response;
    targetItem.status = 'error';

    const nextFileList = updateFileList(targetItem, mergedFileList);

    onInternalChange(targetItem, nextFileList);
  };

  const onFileDrop = (e) => {
    if (e.type === 'dragenter') {
      dragCounter++;
    }
    else if (e.type === 'dragleave') {
      dragCounter--;
    }

    if (dragCounter > 0 && e.type === 'dragleave') e.type = 'dragenter';

    setDragState(e.type);

    if (e.type === 'drop') {
      dragCounter = 0;
      onDrop?.(e);
    }
    else {
      onDrag?.(e);
    }
  };

  // Test needs
  React.useImperativeHandle(ref, () => ({
    onBatchStart: mergedOnBatchStart,
    onSuccess,
    onProgress,
    onError,
    fileList: mergedFileList,
    upload: upload.current,
  }));

  const rcUploadProps = {
    onBatchStart: mergedOnBatchStart,
    onError,
    onProgress,
    onSuccess,
    ...props,
    data,
    multiple,
    action,
    accept,
    disabled,
    beforeUpload,
    onChange: undefined,
  };

  delete rcUploadProps.className;
  delete rcUploadProps.style;

  // Remove id to avoid open by label when trigger is hidden
  // !children: https://github.com/ant-design/ant-design/issues/14298
  // disabled: https://github.com/ant-design/ant-design/issues/16478
  //           https://github.com/ant-design/ant-design/issues/24197
  if (!children || disabled) {
    delete rcUploadProps.id;
  }

  const styles = {
    wrapper: {
      width: '100%',
      height: '100%',
      ...wrapperStyle,
    },
    drag: {
      boxSizing: 'border-box',
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      textAlign: 'center',
      background: K.colors.gray,
      border: `1px dashed ${K.colors.doubleGray}`,
      borderRadius: K.borderRadius,
      cursor: 'pointer',
      transition: 'border-color 1s',
      ...(disabled ? K.disabled : {}),
      ...({borderColor: dragState === 'dragover' ? K.colors.active : K.colors.doubleGray}),
      ...style,
    },
    button: {
      display: 'table',
      width: '100%',
      height: '100%',
      outline: 'none',
      ...(children ? {} : {display: 'none'}),
      ...(disabled ? K.disabled : {}),
      ...style,
    },
    upload: {
      width: '100%',
      height: '100%',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      ...contentStyle,
    },
    disabled: {
      cursor: 'not-allowed',
      color: K.colors.disabled,
    },
  };

  if (type === 'drag') {
    return (
      <span style={styles.wrapper} onDrop={onFileDrop}>
        <div style={styles.drag}>
          <RcUpload {...rcUploadProps} style={styles.upload} ref={upload}>
            <div style={styles.content}>
              {children}
            </div>
          </RcUpload>
        </div>
      </span>
    );
  }
  else {
    const uploadButton = (
      <RcUpload {...rcUploadProps} style={styles.upload} ref={upload}>
        <div style={styles.content}>
          {children}
        </div>
      </RcUpload>
    );

    return (
      <span style={styles.wrapper}>
        {uploadButton}
      </span>
    );
  }
};

const Upload = React.forwardRef(InternalUpload);

if (process.env.NODE_ENV !== 'production') {
  Upload.displayName = 'Upload';
}

export default Upload;